import {createAction, createReducer} from '@reduxjs/toolkit'
import {takeEvery, getContext, put, call} from 'redux-saga/effects'
import {Dictionary, keyBy} from 'lodash'
import User from '../../models/User'
import Api from 'Api'
import {actions as searchActions} from './search'

export type UsersState = {
  users: Dictionary<User>
  total: number
  currentPage: number
  autoResetPasswordResponse: {
    message: string
    newPassword: string
  } | null
  forgotPasswordMessage: string | undefined
  loading: boolean
  error: string | undefined
}
export const initialState: UsersState = {
  users: {},
  total: 0,
  currentPage: 1,
  autoResetPasswordResponse: null,
  forgotPasswordMessage: undefined,
  loading: false,
  error: undefined,
}

export const actions = {
  set: createAction<{users: User[], total?: number, page?: number}>('users/set'),
  get: createAction<void>('users/get'),
  create: createAction<{user: User}>('users/create'),
  delete: createAction<{id: string}>('users/delete'),
  remove: createAction<{id: string}>('users/remove'),
  sendResetPasswordInstructions: createAction<{id: string}>('users/sendResetPasswordInstructions'),
  sendResetPasswordInstructionsByEmail: createAction<{email: string}>(
    'users/sendResetPasswordInstructionsByEmail',
  ),
  resetPassword: createAction<{
    userId: string
    password: string
    resetPasswordToken: string
  }>('users/resetPassword'),
  autoResetPassword: createAction<{userId: string}>('users/autoResetPassword'),
  setLoading: createAction<{loading: boolean}>('auth/setLoading'),
  setError: createAction<{error?: string}>('auth/setError'),
  setAutoResetPassword: createAction<{message: string; newPassword: string}>(
    'users/setAutoResetPassword',
  ),
  setForgotPasswordMessage: createAction<{message: string | undefined}>(
    'users/setForgotPasswordMessage',
  ),
}

export const reducer = createReducer(initialState, {
  [actions.set.type]: (state, action: ReturnType<typeof actions.set>) => {
    const {users, total, page} = action.payload
    return {
      ...state,
      users: keyBy(users, 'id'),
      ...(total !== undefined && {total}),
      ...(page !== undefined && {currentPage: page}),
    }
  },
  [actions.remove.type]: (state, action: ReturnType<typeof actions.remove>) => {
    const {id} = action.payload
    const users = {...state.users}
    delete users[id]
    return {...state, users}
  },
  [actions.setLoading.type]: (state, action: ReturnType<typeof actions.setLoading>) => {
    const {loading} = action.payload
    return {...state, loading}
  },
  [actions.setError.type]: (state, action: ReturnType<typeof actions.setError>) => {
    const {error} = action.payload
    return {...state, error}
  },
  [actions.setAutoResetPassword.type]: (
    state,
    action: ReturnType<typeof actions.setAutoResetPassword>,
  ) => {
    state.autoResetPasswordResponse = {
      message: action.payload.message,
      newPassword: action.payload.newPassword,
    }
  },
  [actions.setForgotPasswordMessage.type]: (
    state,
    action: ReturnType<typeof actions.setForgotPasswordMessage>,
  ) => {
    state.forgotPasswordMessage = action.payload.message
  },
})

const deleteSaga = function*(action: ReturnType<typeof actions.delete>) {
  const {id} = action.payload
  const api: Api = yield getContext('api')
  yield put(actions.setLoading({loading: true}))
  yield put(actions.setError({error: undefined}))
  try {
    yield call(api.deleteUser, id)
    yield put(actions.remove({id}))
    yield put(searchActions.refreshSearch())
  } catch (e) {
    const error = e as any
    yield put(actions.setError({error: error.message}))
  }
  yield put(actions.setLoading({loading: false}))
}

const createSaga = function*(action: ReturnType<typeof actions.create>) {
  const {user} = action.payload
  const api: Api = yield getContext('api')
  yield put(actions.setLoading({loading: true}))
  yield put(actions.setError({error: undefined}))
  try {
    yield call(api.createUser, user)
    yield put(searchActions.refreshSearch())
  } catch (e) {
    const error = e as any
    yield put(actions.setError({error: error.message}))
  }
  yield put(actions.setLoading({loading: false}))
}

const getSaga = function*() {
  const api: Api = yield getContext('api')
  try {
    const {users, total, page} = yield call(api.getUsersPage, 1, 100)
    yield put(actions.set({users, total, page}))
  } catch (e) {
    const error = e as any
    yield put(actions.setError({error: error?.message ?? 'Error loading users'}))
  }
}

const sendResetPasswordInstructionsSaga = function*(
  action: ReturnType<typeof actions.sendResetPasswordInstructions>,
) {
  const {id} = action.payload
  const api: Api = yield getContext('api')
  const response = yield call(api.sendResetPasswordInstructions, id)
  if (response.message) {
    alert(response.message)
  }
}

const sendResetPasswordInstructionsByEmailSaga = function*(
  action: ReturnType<typeof actions.sendResetPasswordInstructionsByEmail>,
) {
  const {email} = action.payload
  const api: Api = yield getContext('api')
  yield put(actions.setLoading({loading: true}))
  try {
    yield call(api.sendResetPasswordInstructionsByEmail, email)
  } catch (e) {
    // Ignored: always show the same generic message below, regardless of
    // whether the email is registered, to avoid leaking account existence
    // to this unauthenticated caller.
  }
  yield put(
    actions.setForgotPasswordMessage({
      message: 'If that email is registered, password reset instructions have been sent.',
    }),
  )
  yield put(actions.setLoading({loading: false}))
}

const resetPasswordSaga = function*(action: ReturnType<typeof actions.resetPassword>) {
  const {password, userId, resetPasswordToken} = action.payload
  const api: Api = yield getContext('api')
  yield put(actions.setLoading({loading: true}))
  const response = yield call(api.resetUserPassword, userId, password, resetPasswordToken)
  // response.status === 'error' is how Api.resetUserPassword signals a failed
  // request (see its catch block) — matches the convention used in
  // autoResetPasswordSaga. Checking this instead of allowlisting specific
  // message strings means an unrecognized backend error can't be mistaken
  // for success.
  yield put(actions.setError({error: response.status === 'error' ? response.message : undefined}))
  yield put(actions.setLoading({loading: false}))
}

const autoResetPasswordSaga = function*(action: ReturnType<typeof actions.autoResetPassword>) {
  const {userId} = action.payload
  const api: Api = yield getContext('api')

  yield put(actions.setLoading({loading: true}))
  yield put(actions.setAutoResetPassword({message: '', newPassword: ''}))

  try {
    const response = yield call(api.autoResetUserPassword, userId)

    if (response.status === 'error') {
      yield put(actions.setError({error: response.error}))
    } else {
      yield put(
        actions.setAutoResetPassword({
          message: response.message,
          newPassword: response.newPassword,
        }),
      )
    }
  } catch (err) {
    yield put(actions.setError({error: (err as any).message || 'Unexpected error'}))
  }

  yield put(actions.setLoading({loading: false}))
}

export const saga = function*() {
  yield takeEvery(actions.get, getSaga)
  yield takeEvery(actions.create, createSaga)
  yield takeEvery(actions.delete, deleteSaga)
  yield takeEvery(actions.sendResetPasswordInstructions, sendResetPasswordInstructionsSaga)
  yield takeEvery(
    actions.sendResetPasswordInstructionsByEmail,
    sendResetPasswordInstructionsByEmailSaga,
  )
  yield takeEvery(actions.resetPassword, resetPasswordSaga)
  yield takeEvery(actions.autoResetPassword, autoResetPasswordSaga)
}
